import React, { useState, useEffect, useRef } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import ExamPage from './pages/ExamPage';
import ResultsPage from './pages/ResultsPage';
import AdminPanel from './pages/AdminPanel';
import PricingPage from './pages/PricingPage';
import PaymentSuccess from './pages/PaymentSuccess';
import ProfessionSelector from './pages/ProfessionSelector';
import PlanSelector from './pages/PlanSelector';
import TutorChat from './components/TutorChat';
import PracticeFailedPage from './pages/PracticeFailedPage';
import CommunityPage from './pages/CommunityPage';
import FlashcardsPage from './pages/FlashcardsPage';
import StudyPlanPage from './pages/StudyPlanPage';
import LegalPage from './pages/LegalPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import InstitutionalDashboard from './pages/InstitutionalDashboard';
import ProfilePage from './pages/ProfilePage';
import CreditsPage from './pages/CreditsPage';
import CountrySelector from './pages/CountrySelector';
import OfflineTestPage from './pages/OfflineTestPage';
import LibraryPage from './pages/LibraryPage';
import LogoDownloadPage from './pages/LogoDownloadPage';

// Ignore MetaMask errors
window.addEventListener('error', (e) => {
  if (e.message?.includes('MetaMask') || e.message?.includes('ethereum')) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }
});

window.addEventListener('unhandledrejection', (e) => {
  if (e.reason?.message?.includes('MetaMask') || e.reason?.message?.includes('ethereum')) {
    e.preventDefault();
    return false;
  }
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.baseURL = BACKEND_URL;
axios.defaults.withCredentials = true;

// LegacyAuthCallback component for handling Emergent Google OAuth session_id (legacy)
const LegacyAuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing');
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async (attempt = 0) => {
      // Extract session_id from URL fragment
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        toast.error('No se encontró la sesión de autenticación');
        navigate('/auth');
        return;
      }

      const sessionId = sessionIdMatch[1];
      
      try {
        setStatus('processing');
        // Call backend to process session with timeout
        const response = await axios.get(`${API}/auth/session?session_id=${sessionId}`, {
          timeout: 15000 // 15 second timeout
        });
        
        // Store token
        localStorage.setItem('session_token', response.data.session_token);
        
        setStatus('success');
        toast.success('¡Bienvenido!');
        
        // Check if user needs to select profession
        if (!response.data.user.selected_profession) {
          navigate('/select-profession', { state: { user: response.data.user } });
        } else {
          navigate('/dashboard', { state: { user: response.data.user } });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        
        // Retry logic - up to 2 retries
        if (attempt < 2) {
          setRetryCount(attempt + 1);
          setStatus('retrying');
          // Wait 1 second before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
          return processSession(attempt + 1);
        }
        
        // Get specific error message
        let errorMsg = 'Error al procesar la autenticación';
        if (error.response?.data?.detail) {
          errorMsg = error.response.data.detail;
        } else if (error.code === 'ECONNABORTED') {
          errorMsg = 'Tiempo de espera agotado. Por favor, intenta de nuevo.';
        } else if (error.message?.includes('Network Error')) {
          errorMsg = 'Error de conexión. Verifica tu internet e intenta de nuevo.';
        }
        
        setStatus('error');
        toast.error(errorMsg);
        
        // Wait 2 seconds before redirecting so user can see the error
        setTimeout(() => navigate('/auth'), 2000);
      }
    };

    processSession();
  }, [navigate, location]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="text-center bg-white p-8 rounded-2xl shadow-lg border border-slate-200 max-w-md">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Procesando autenticación...</p>
          </>
        )}
        {status === 'retrying' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
            <p className="text-slate-600">Reintentando conexión... ({retryCount}/2)</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-green-600 font-medium">¡Autenticación exitosa!</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-600 font-medium">Error de autenticación</p>
            <p className="text-slate-500 text-sm mt-2">Redirigiendo...</p>
          </>
        )}
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (!token) {
          setIsAuthenticated(false);
          return;
        }

        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem('session_token');
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, [location, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return React.cloneElement(children, { user });
};

// AdminRoute - Only for super admins
const AdminRoute = ({ children }) => {
  const [authState, setAuthState] = useState({ loading: true, isAdmin: false, user: null });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (!token) {
          setAuthState({ loading: false, isAdmin: false, user: null });
          return;
        }

        // First get user info
        const userResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Then check if admin
        const adminResponse = await axios.get(`${API}/auth/check-admin`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setAuthState({ 
          loading: false, 
          isAdmin: adminResponse.data.is_admin, 
          user: userResponse.data 
        });
      } catch (error) {
        console.error('Admin check error:', error);
        setAuthState({ loading: false, isAdmin: false, user: null });
      }
    };
    checkAdmin();
  }, [location]);

  if (authState.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!authState.isAdmin) {
    toast.error('Acceso denegado. Solo administradores autorizados.');
    return <Navigate to="/dashboard" replace />;
  }

  return React.cloneElement(children, { user: authState.user });
};

// InstitutionalRoute - For institutional admins
const InstitutionalRoute = ({ children }) => {
  const [authState, setAuthState] = useState({ loading: true, isInstitutional: false, user: null });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkInstitutional = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (!token) {
          setAuthState({ loading: false, isInstitutional: false, user: null });
          return;
        }

        const userResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const user = userResponse.data;
        // Check if user is institutional admin or super admin
        const isInstitutional = user.role === 'institutional_admin' || 
                               user.institution_id || 
                               user.email === 'santaliestralimited@gmail.com';

        setAuthState({ 
          loading: false, 
          isInstitutional, 
          user 
        });
      } catch (error) {
        console.error('Institutional check error:', error);
        setAuthState({ loading: false, isInstitutional: false, user: null });
      }
    };
    checkInstitutional();
  }, [location]);

  if (authState.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!authState.isInstitutional) {
    toast.error('Acceso denegado. Solo administradores institucionales.');
    return <Navigate to="/dashboard" replace />;
  }

  return React.cloneElement(children, { user: authState.user });
};

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Emergent Google OAuth callback - legacy)
  // This MUST be checked synchronously during render, NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <LegacyAuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/select-country"
        element={
          <ProtectedRoute>
            <CountrySelector />
          </ProtectedRoute>
        }
      />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route
        path="/select-profession"
        element={
          <ProtectedRoute>
            <ProfessionSelector />
          </ProtectedRoute>
        }
      />
      <Route
        path="/select-plan"
        element={
          <ProtectedRoute>
            <PlanSelector />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/exam"
        element={
          <ProtectedRoute>
            <ExamPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/results/:examId"
        element={
          <ProtectedRoute>
            <ResultsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        }
      />
      <Route
        path="/institutional"
        element={
          <InstitutionalRoute>
            <InstitutionalDashboard />
          </InstitutionalRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/credits"
        element={
          <ProtectedRoute>
            <CreditsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/practice-failed"
        element={
          <ProtectedRoute>
            <PracticeFailedPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/community"
        element={
          <ProtectedRoute>
            <CommunityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/flashcards"
        element={
          <ProtectedRoute>
            <FlashcardsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/study-plan"
        element={
          <ProtectedRoute>
            <StudyPlanPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/offline-test"
        element={
          <ProtectedRoute>
            <OfflineTestPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <LibraryPage />
          </ProtectedRoute>
        }
      />
      <Route path="/logos" element={<LogoDownloadPage />} />
      <Route path="/aviso-legal" element={<LegalPage />} />
      <Route path="/privacidad" element={<PrivacyPage />} />
      <Route path="/contacto" element={<ContactPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <TutorChat />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
